import {useCallback, useEffect, useRef, useState} from "react";
import {useIntegrator} from "../../integrator.context";
import {useApp} from "../../app.context";
import {useAccess} from "../../utils/useAccess";
import {ClassifiedImage} from "../../utils/imageClassification";
import {clampScale, effectiveScale} from "../../utils/imageScale";
import {deleteRequest, get, post} from "../../utils/http.client";
import {apiBindings} from "../../utils/constants";
import {analyzeSprite, maskAt, SpriteAnalysis} from "../../utils/silhouette";
import {
    TILE_HALF_W,
    ZOOM_MIN, ZOOM_MAX, ZOOM_PER_PIXEL, ZOOM_PER_WHEEL, ZOOM_PER_PINCH,
    ViewTransform, DEFAULT_VIEW, clampZoom, tileCenter, snapToTile, gridCorner, gridAt,
} from "../../utils/isoCanvas";

// The Workshop — the main area of the Images tab, a staging area where sprites
// can be arranged the way the game arranges them (isometric grid) to judge
// whether pivots and scaling will hold up in-game. The images grid and the
// image-details pane sit in the sidebar to its right.
//
// Canvas-rendered: one view transform pans/zooms everything together, and
// sprites composite back-to-front (by tile row) like the client does.
//
// Controls: right-drag pans, middle-drag zooms (right/up = in, left/down =
// out, anchored on the point where the drag started). Drag a thumbnail in
// from the Images grid to place it; LMB selects/moves placed images;
// Del/Backspace removes the selection.
//
// Placed images anchor their PIVOT to the centre of a tile diamond — the
// pivot is the grid snap point, so pivot edits in the flyout move the
// sprite relative to its tile in real time.

// Projection constants and view-transform math live in utils/isoCanvas.ts,
// shared with the Map Editor so the two canvases can never drift apart.

// All images render this wide (world px) regardless of natural size, aspect
// preserved — pinned to the grid diamond width so a tile sprite whose top
// face spans its full width lands exactly on one cell at scale 1. (Was a
// stray 128 left over from when the grid itself was 128 wide.) Later this
// becomes per-kind (units ~80, buildings ~96, ...).
const ITEM_DEFAULT_W = TILE_HALF_W * 2;

const SCALE_PER_PIXEL = 0.005;  // Alt-drag: right/up bigger, exp like zoom

const LINE_COLOR = 'rgba(255, 255, 255, 0.15)';
const AXIS_COLOR = 'rgba(255, 255, 255, 0.4)';
const SELECT_COLOR = 'magenta';

// Selection silhouette outline: magenta line over a dark casing so it reads on
// both light and dark sprites. Widths are screen-space (CSS px) and constant at
// every zoom — see utils/silhouette.ts.
const OUTLINE_WIDTH = 2;
const OUTLINE_CASING_WIDTH = 4;
const OUTLINE_CASING_COLOR = 'rgba(0, 0, 0, 0.55)';

const VIEW_STORAGE_KEY = 'rift.editor.workshopView';
const ITEMS_STORAGE_KEY = 'rift.editor.workshopItems';
const ITEMS_VERSION_STORAGE_KEY = 'rift.editor.workshopItemsVersion';
const NAME_STORAGE_KEY = 'rift.editor.workshopName';

// Bumped when stored (u, v) need reinterpreting. v2 is the isometric axis-sign
// fix (see isoCanvas.ts): the canvas used to draw a growing (u+v) DOWN the
// screen, so every layout saved before it is mirrored top-to-bottom under the
// corrected projection. Migrating puts each item back on the exact world
// position it already occupied, so arrangements stay pixel-identical while
// their axes now agree with the game.
const WORKSHOP_SCHEMA_VERSION = 2;

// Solve tileCenter_old(u, v) == tileCenter_new(u', v') and you get
// (u', v') = (-v-1, -u-1) — the reflection that cancels the old sign error.
// It is its own inverse, so applying it twice is a no-op; the version stamp is
// what actually stops a second pass, the involution is just a safety net.
function migrateItems(items: WorkshopItem[], fromVersion: number): WorkshopItem[] {
    if (fromVersion >= WORKSHOP_SCHEMA_VERSION) return items;
    return items.map(item => ({...item, u: -item.v - 1, v: -item.u - 1}));
}

// A payload with no readable version predates versioning, so it is v1.
function payloadVersion(raw: unknown): number {
    return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : 1;
}

// On macOS the Alt/Ctrl keys are labelled "Option"/"Cmd" — match the keycaps in
// help text (the pivot gesture already accepts both Ctrl and Cmd).
const IS_MAC = typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgent);
const ALT_KEY_LABEL = IS_MAC ? 'Option' : 'Alt';
const CTRL_KEY_LABEL = IS_MAC ? 'Cmd' : 'Ctrl';

// Mirrors the backend's blob-name validation (Workshops.cs).
const WORKSHOP_NAME_PATTERN = /^[\w\- ]{1,64}$/;

// The shared default workshop: auto-loaded for users who haven't opened one of
// their own, and editable (save/delete) by Admins only. Matched case-insensitively
// to mirror the backend guard.
const DEFAULT_WORKSHOP_NAME = 'default';
const isDefaultName = (name: string) => name.trim().toLowerCase() === DEFAULT_WORKSHOP_NAME;

// Within one grid location, images stack in game-like layer order:
// tile art at the bottom, then buildings, units, resources, and finally
// anything else (icons, UI sprites). Sections come from the image
// classification (the same source the Images grid sections use).
const SECTION_DRAW_PRIORITY: Record<string, number> = {
    'tiles': 0,
    'buildings': 1,
    'units': 2,
    'resources': 3,
};
const SECTION_DRAW_PRIORITY_OTHER = 4;

// One placed sprite, pinned by pivot to tile (u, v). Editor-local scratch
// state — persisted to localStorage, never to branch data.
interface WorkshopItem {
    id: number;
    imageKey: string;
    u: number;
    v: number;
}

function loadView(): ViewTransform {
    try {
        const v = JSON.parse(localStorage.getItem(VIEW_STORAGE_KEY) ?? '');
        if (typeof v.centerX === 'number' && typeof v.centerY === 'number'
            && typeof v.zoom === 'number' && v.zoom >= ZOOM_MIN && v.zoom <= ZOOM_MAX) {
            return v;
        }
    } catch { /* corrupt/absent — use default */ }
    return DEFAULT_VIEW;
}

// Shape-validate items from any untrusted source (localStorage, .workshop
// blobs). Deliberately does NOT check image keys against the branch —
// workshops are branch-agnostic; unresolvable keys render as placeholders.
function sanitizeItems(raw: unknown): WorkshopItem[] {
    if (!Array.isArray(raw)) return [];
    return raw.filter(i => typeof i?.id === 'number' && typeof i?.imageKey === 'string'
        && Number.isInteger(i?.u) && Number.isInteger(i?.v));
}

function loadItems(): WorkshopItem[] {
    try {
        const stored = sanitizeItems(JSON.parse(localStorage.getItem(ITEMS_STORAGE_KEY) ?? ''));
        const stamp = Number(localStorage.getItem(ITEMS_VERSION_STORAGE_KEY));
        return migrateItems(stored, payloadVersion(stamp));
    } catch { /* corrupt/absent — start empty */ }
    return [];
}

function clamp01(n: number): number {
    return Math.min(1, Math.max(0, n));
}

type Gesture =
    | {kind: 'pan' | 'zoom'; startX: number; startY: number; view0: ViewTransform}
    | {
        // Ctrl-drag on the selected image: slide it under its fixed
        // tile-centre anchor; the offset back-solves into the pivot,
        // snapped to the source image's pixel grid ("pixel by pixel").
        kind: 'pivot';
        imageKey: string;
        startPivot: {x: number; y: number};
        startWX: number;       // world pointer pos at press
        startWY: number;
        w: number;             // image world dims (constant during drag)
        h: number;
        natW: number;          // source pixels, for snapping
        natH: number;
        last?: {x: number; y: number};   // most recent previewed pivot
    }
    | {
        // Alt-drag on the selected image: right/up grows, left/down
        // shrinks; exponential, snapped to the 0.01 scale step.
        kind: 'scale';
        imageKey: string;
        startScale: number;
        startX: number;        // client coords at press
        startY: number;
        last?: number;         // most recent previewed scale
    }
    | {
        kind: 'move';
        itemId: number;
        grabDX: number;
        grabDY: number;
        // Click-cycling support: a press that never moves past the slop
        // radius is a click, and clicking a stack repeatedly cycles through
        // the images under the cursor (front to back).
        startX: number;
        startY: number;
        moved: boolean;
        hitIds: number[];        // items under the press point, front first
        wasSelected: boolean;    // press landed on the already-selected item
    };

// Pixel slop before a press counts as a drag rather than a click.
const CLICK_SLOP_PX = 4;

interface WorkshopViewProps {
    // Image key currently being dragged from the thumbnail grid, if any —
    // lets dragover render a snap ghost (dataTransfer payloads are sealed
    // until drop).
    draggingImageKey?: string;
    // In-flight pivot from the flyout's crosshair drag — overrides the
    // stored pivot for matching instances so they track the drag live.
    transientPivot?: {key: string; x: number; y: number};
    // In-flight scale from the flyout's Scale slider — same live-preview
    // contract as transientPivot.
    transientScale?: {key: string; value: number};
    // Derived image identity/sections (computed once by the Images tab).
    classification: Map<string, ClassifiedImage>;
    // Make an image the active flyout selection (grab-in and LMB-select).
    onSelectImage(key: string): void;
    // Ctrl-drag pivot editing: preview live (no command), commit on release
    // (one UpdateImageCommand). Same transient-then-commit contract the
    // flyout crosshair uses.
    onPreviewPivot(key: string, x: number, y: number): void;
    onCommitPivot(key: string, x: number, y: number): void;
    // Alt-drag scale editing: same contract, via the flyout's scale channel.
    onPreviewScale(key: string, value: number): void;
    onCommitScale(key: string, value: number): void;
}

function WorkshopView(props: WorkshopViewProps) {
    const {state} = useIntegrator();
    const {getImageUrl, confirm, choose, setNotification} = useApp();
    const {canServerWrite, canSaveDefaultWorkshop} = useAccess();

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [view, setView] = useState<ViewTransform>(loadView);
    const [items, setItems] = useState<WorkshopItem[]>(loadItems);
    const [selectedItemId, setSelectedItemId] = useState<number | undefined>(undefined);
    const [ghost, setGhost] = useState<{u: number; v: number} | undefined>(undefined);
    const [showHelp, setShowHelp] = useState(false);
    // Tooltip shown when hovering a placeholder (unresolvable image): which key
    // it's looking for and why it's missing. x/y are canvas-local px.
    const [hoverInfo, setHoverInfo] = useState<{key: string; reason: string; x: number; y: number} | undefined>(undefined);
    // Name this workshop was loaded from / last saved as, if any.
    const [workshopName, _setWorkshopName] = useState<string | undefined>(
        () => localStorage.getItem(NAME_STORAGE_KEY) ?? undefined);
    // Save dialog; continueLoad chains into the load picker after a
    // successful save-before-load.
    const [saveDialog, setSaveDialog] = useState<{name: string; continueLoad: boolean} | undefined>(undefined);
    // Load picker: full name list (sorted), live filter, current selection.
    const [loadDialog, setLoadDialog] = useState<{names: string[]; filter: string; selected?: string} | undefined>(undefined);
    // Two-step Clear: first click arms ("Clear?"), second click clears.
    // Disarms on blur or after a few seconds without the second click.
    const [clearArmed, setClearArmed] = useState(false);
    const clearArmTimer = useRef<number | undefined>(undefined);
    // Two-step Delete in the load picker, same semantics.
    const [deleteArmed, setDeleteArmed] = useState(false);
    const deleteArmTimer = useRef<number | undefined>(undefined);
    const gestureRef = useRef<Gesture | undefined>(undefined);

    useEffect(() => () => {
        window.clearTimeout(clearArmTimer.current);
        window.clearTimeout(deleteArmTimer.current);
    }, []);

    // Mirrors for persistence/handlers (closures may hold stale renders).
    const viewRef = useRef(view);
    viewRef.current = view;
    const itemsRef = useRef(items);
    itemsRef.current = items;

    function persistItems(next: WorkshopItem[]) {
        setItems(next);
        localStorage.setItem(ITEMS_STORAGE_KEY, JSON.stringify(next));
        // Stamped alongside, so the axis migration runs exactly once per browser.
        localStorage.setItem(ITEMS_VERSION_STORAGE_KEY, String(WORKSHOP_SCHEMA_VERSION));
    }

    function setWorkshopName(name: string | undefined) {
        _setWorkshopName(name);
        if (name) localStorage.setItem(NAME_STORAGE_KEY, name);
        else localStorage.removeItem(NAME_STORAGE_KEY);
    }

    // On first open with no workshop of our own (nothing loaded and nothing in
    // progress), load the shared "default" workshop if one exists. Silent when
    // it doesn't — the workshop just stays empty, exactly as before.
    useEffect(() => {
        if (itemsRef.current.length > 0 || workshopName !== undefined) return;
        let cancelled = false;
        (async () => {
            const response = await get(`${apiBindings.api}getWorkshop?name=${encodeURIComponent(DEFAULT_WORKSHOP_NAME)}`, undefined);
            if (cancelled || !response.success) return;   // no default → stay empty
            const loaded = migrateItems(
                sanitizeItems(response.data?.items), payloadVersion(response.data?.version));
            if (loaded.length === 0) return;
            persistItems(loaded);
            setSelectedItemId(undefined);
            setWorkshopName(DEFAULT_WORKSHOP_NAME);
        })();
        return () => { cancelled = true; };
        // Mount-only: auto-load applies only when the workshop starts empty/unnamed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- bitmap cache ------------------------------------------------------
    // One Image element per image key, tagged with the URL it was loaded from;
    // a load triggers a redraw via drawRef so sprites pop in as they arrive.
    // When an image is replaced (a re-upload mints a new fileName → new URL),
    // the cached entry's URL no longer matches, so we reload instead of showing
    // the stale bitmap. Failures are tracked by the URL that failed (not just
    // the key), so a replacement URL isn't treated as permanently broken — and
    // keys absent from the branch (workshops are branch-agnostic) render as
    // placeholders.
    const bitmapsRef = useRef(new Map<string, {img: HTMLImageElement; url: string}>());
    const failedUrlsRef = useRef(new Map<string, string>());
    const drawRef = useRef<() => void>(() => undefined);

    // Whether the image currently resolved for `key` is known to have failed.
    const isFailed = useCallback((key: string) =>
        failedUrlsRef.current.get(key) === getImageUrl(key), [getImageUrl]);

    const getBitmap = useCallback((key: string): HTMLImageElement | undefined => {
        const url = getImageUrl(key);
        const cached = bitmapsRef.current.get(key);
        if (cached && cached.url === url)
            return cached.img.complete && cached.img.naturalWidth > 0 ? cached.img : undefined;
        const img = new Image();
        img.onload = () => drawRef.current();
        img.onerror = () => {
            failedUrlsRef.current.set(key, url);
            drawRef.current();
        };
        img.src = url;
        bitmapsRef.current.set(key, {img, url});
        return undefined;
    }, [getImageUrl]);

    // ---- sprite analysis cache ---------------------------------------------
    // Per-image outline contours + pick mask, feeding both the selection
    // outline and pixel-perfect hit-testing. Computed from a SEPARATE
    // crossOrigin image load — kept apart from the bitmap cache so the main
    // render path is never affected by CORS. Keyed by resolved URL so a
    // re-upload under the same key refreshes. null = computed but unusable
    // (tainted canvas / empty) → callers fall back to the bounding box.
    const analysisRef = useRef(new Map<string, SpriteAnalysis | null>());
    const analysisLoadingRef = useRef(new Set<string>());

    const getAnalysis = useCallback((key: string): SpriteAnalysis | null | undefined => {
        const url = getImageUrl(key);
        if (analysisRef.current.has(url)) return analysisRef.current.get(url);
        if (analysisLoadingRef.current.has(url)) return undefined;   // in flight
        analysisLoadingRef.current.add(url);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            analysisRef.current.set(url, analyzeSprite(img) ?? null);
            analysisLoadingRef.current.delete(url);
            drawRef.current();
        };
        img.onerror = () => {
            analysisRef.current.set(url, null);
            analysisLoadingRef.current.delete(url);
            drawRef.current();
        };
        img.src = url;
        return undefined;
    }, [getImageUrl]);

    // Painter's order: back-to-front by grid row (u+v), left-to-right
    // within a row, then by section layer within one grid location
    // (tiles → buildings → units → resources → other), placement order last.
    //
    // Back-to-front is DESCENDING (u+v): with the game's +Y-up axis a higher
    // row sits farther from the camera (see the axis-sign note in isoCanvas.ts).
    const orderItems = useCallback((list: WorkshopItem[]): WorkshopItem[] => {
        const priority = (key: string) =>
            SECTION_DRAW_PRIORITY[props.classification.get(key)?.section ?? '']
            ?? SECTION_DRAW_PRIORITY_OTHER;
        return [...list].sort((a, b) =>
            (b.u + b.v) - (a.u + a.v)
            || (a.u - a.v) - (b.u - b.v)
            || priority(a.imageKey) - priority(b.imageKey)
            || a.id - b.id);
    }, [props.classification]);

    // World-space rect of a placed sprite: ITEM_DEFAULT_W wide, aspect kept,
    // positioned so the image's pivot lands on its tile centre. Unity pivots
    // are bottom-left-origin, canvas is top-down — hence the (1 - pivotY).
    const itemWorldRect = useCallback((item: {imageKey: string; u: number; v: number}) => {
        const meta = state.images[item.imageKey];
        // Key unknown to this branch, or the file 404'd: small placeholder
        // box on the tile so the item stays visible/selectable/deletable.
        if (!meta || isFailed(item.imageKey)) {
            const [ax, ay] = tileCenter(item.u, item.v);
            return {x: ax - 20, y: ay - 20, w: 40, h: 40, ax, ay, bmp: undefined};
        }
        const bmp = getBitmap(item.imageKey);
        if (!bmp) return undefined;
        const scale = props.transientScale?.key === item.imageKey
            ? props.transientScale.value
            : effectiveScale(meta?.scale);
        const w = ITEM_DEFAULT_W * scale;
        const h = bmp.naturalHeight * (w / bmp.naturalWidth);
        const transient = props.transientPivot?.key === item.imageKey ? props.transientPivot : undefined;
        const pivotX = transient?.x ?? meta?.offsetX ?? 0.5;
        const pivotY = transient?.y ?? meta?.offsetY ?? 0.5;
        const [ax, ay] = tileCenter(item.u, item.v);
        return {x: ax - pivotX * w, y: ay - (1 - pivotY) * h, w, h, bmp, ax, ay};
    }, [getBitmap, isFailed, state, props.transientPivot, props.transientScale]);

    // ---- rendering ---------------------------------------------------------

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        if (w <= 0 || h <= 0) return;
        const dpr = window.devicePixelRatio || 1;
        if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        ctx.imageSmoothingEnabled = false;

        const {centerX, centerY, zoom} = view;
        const toScreenX = (wx: number) => (wx - centerX) * zoom + w / 2;
        const toScreenY = (wy: number) => (wy - centerY) * zoom + h / 2;
        const toWorld = (sx: number, sy: number): [number, number] =>
            [(sx - w / 2) / zoom + centerX, (sy - h / 2) / zoom + centerY];

        // Grid lines are the two families of iso axes: constant-u lines run
        // along +v, constant-v lines along +u. Invert the viewport corners
        // into (u, v) to find which lines are visible.
        const corners = [toWorld(0, 0), toWorld(w, 0), toWorld(0, h), toWorld(w, h)];
        const us = corners.map(([x, y]) => gridAt(x, y)[0]);
        const vs = corners.map(([x, y]) => gridAt(x, y)[1]);
        const uMin = Math.floor(Math.min(...us)), uMax = Math.ceil(Math.max(...us));
        const vMin = Math.floor(Math.min(...vs)), vMax = Math.ceil(Math.max(...vs));

        ctx.lineWidth = 1;
        for (let u = uMin; u <= uMax; u++) {
            ctx.strokeStyle = u === 0 ? AXIS_COLOR : LINE_COLOR;
            ctx.beginPath();
            const [ax, ay] = gridCorner(u, vMin);
            const [bx, by] = gridCorner(u, vMax);
            ctx.moveTo(toScreenX(ax), toScreenY(ay));
            ctx.lineTo(toScreenX(bx), toScreenY(by));
            ctx.stroke();
        }
        for (let v = vMin; v <= vMax; v++) {
            ctx.strokeStyle = v === 0 ? AXIS_COLOR : LINE_COLOR;
            ctx.beginPath();
            const [ax, ay] = gridCorner(uMin, v);
            const [bx, by] = gridCorner(uMax, v);
            ctx.moveTo(toScreenX(ax), toScreenY(ay));
            ctx.lineTo(toScreenX(bx), toScreenY(by));
            ctx.stroke();
        }

        // Sprites, back-to-front (painter's algorithm; see orderItems). The
        // selected sprite's outline is deferred so it draws on top of every
        // sprite, not just the ones painted before it.
        const ordered = orderItems(items);
        let selectedRender: {item: WorkshopItem; r: NonNullable<ReturnType<typeof itemWorldRect>>} | undefined;
        for (const item of ordered) {
            const r = itemWorldRect(item);
            if (!r) continue;
            if (r.bmp) {
                ctx.drawImage(r.bmp, toScreenX(r.x), toScreenY(r.y), r.w * zoom, r.h * zoom);
            } else {
                // Unresolvable image (key not in this branch / file missing).
                ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 3]);
                ctx.strokeRect(toScreenX(r.x), toScreenY(r.y), r.w * zoom, r.h * zoom);
                ctx.setLineDash([]);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
                ctx.font = `${Math.max(10, 16 * zoom)}px sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('?', toScreenX(r.x + r.w / 2), toScreenY(r.y + r.h / 2));
            }
            if (item.id === selectedItemId) selectedRender = {item, r};
        }

        // Drop ghost while dragging a thumbnail over the canvas.
        if (ghost && props.draggingImageKey) {
            const r = itemWorldRect({imageKey: props.draggingImageKey, u: ghost.u, v: ghost.v});
            if (r && r.bmp) {
                ctx.globalAlpha = 0.5;
                ctx.drawImage(r.bmp, toScreenX(r.x), toScreenY(r.y), r.w * zoom, r.h * zoom);
                ctx.globalAlpha = 1;
            }
        }

        // Selection highlight, drawn last so it sits on top of everything.
        if (selectedRender) {
            const {item, r} = selectedRender;
            // Magenta silhouette outline, hugging the sprite's alpha edge.
            // Built in screen space from cached image-space contours, then
            // stroked at a constant width (zoom-independent thickness).
            const analysis = r.bmp ? getAnalysis(item.imageKey) : null;
            if (analysis && analysis.loops.length > 0) {
                const path = new Path2D();
                for (const loop of analysis.loops) {
                    for (let i = 0; i < loop.length; i++) {
                        const sx = toScreenX(r.x + (loop[i].x / analysis.natural.w) * r.w);
                        const sy = toScreenY(r.y + (loop[i].y / analysis.natural.h) * r.h);
                        if (i === 0) path.moveTo(sx, sy); else path.lineTo(sx, sy);
                    }
                    path.closePath();
                }
                ctx.lineJoin = 'round';
                ctx.lineCap = 'round';
                ctx.strokeStyle = OUTLINE_CASING_COLOR;
                ctx.lineWidth = OUTLINE_CASING_WIDTH;
                ctx.stroke(path);
                ctx.strokeStyle = SELECT_COLOR;
                ctx.lineWidth = OUTLINE_WIDTH;
                ctx.stroke(path);
            } else {
                // No silhouette (loading, missing image, or unreadable
                // pixels): fall back to the bounding box.
                ctx.strokeStyle = SELECT_COLOR;
                ctx.lineWidth = 1;
                ctx.strokeRect(toScreenX(r.x) - 1, toScreenY(r.y) - 1, r.w * zoom + 2, r.h * zoom + 2);
            }
            // Pivot marker on the anchored tile centre.
            ctx.strokeStyle = SELECT_COLOR;
            ctx.lineWidth = 1;
            const px = toScreenX(r.ax), py = toScreenY(r.ay);
            ctx.beginPath();
            ctx.moveTo(px - 5, py); ctx.lineTo(px + 5, py);
            ctx.moveTo(px, py - 5); ctx.lineTo(px, py + 5);
            ctx.stroke();
        }
    }, [view, items, selectedItemId, ghost, props.draggingImageKey, itemWorldRect, orderItems, getAnalysis]);

    drawRef.current = draw;

    useEffect(() => {
        draw();
    }, [draw]);

    // Warm the analysis cache for every placed image so the first click on a
    // sprite is already pixel-perfect (and its outline appears instantly)
    // instead of falling back to the bounding box while the mask loads.
    useEffect(() => {
        const seen = new Set<string>();
        for (const item of items) {
            if (seen.has(item.imageKey)) continue;
            seen.add(item.imageKey);
            getAnalysis(item.imageKey);
        }
    }, [items, getAnalysis]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const observer = new ResizeObserver(() => drawRef.current());
        observer.observe(canvas);
        return () => observer.disconnect();
    }, []);

    // Wheel zoom, anchored on the cursor. Native listener (passive: false)
    // because React's onWheel can't reliably preventDefault page scroll.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        function onWheel(e: WheelEvent) {
            e.preventDefault();
            const v = viewRef.current;
            // ctrlKey set => trackpad pinch-to-zoom (the browser synthesizes
            // ctrl+wheel for pinch); otherwise a real wheel / two-finger scroll.
            const perDelta = e.ctrlKey ? ZOOM_PER_PINCH : ZOOM_PER_WHEEL;
            const zoom = clampZoom(v.zoom * Math.exp(-e.deltaY * perDelta));
            if (zoom === v.zoom) return;
            const rect = canvas!.getBoundingClientRect();
            const ax = e.clientX - rect.left - rect.width / 2;   // anchor, centre-relative
            const ay = e.clientY - rect.top - rect.height / 2;
            const wx = ax / v.zoom + v.centerX;
            const wy = ay / v.zoom + v.centerY;
            const next = {zoom, centerX: wx - ax / zoom, centerY: wy - ay / zoom};
            setView(next);
            localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(next));
        }
        canvas.addEventListener('wheel', onWheel, {passive: false});
        return () => canvas.removeEventListener('wheel', onWheel);
    }, []);

    // ---- selection / removal ----------------------------------------------

    useEffect(() => {
        if (selectedItemId === undefined) return;
        function onKey(e: KeyboardEvent) {
            if (e.key !== 'Delete' && e.key !== 'Backspace') return;
            const t = e.target as HTMLElement;
            if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t.isContentEditable) return;
            persistItems(itemsRef.current.filter(i => i.id !== selectedItemId));
            setSelectedItemId(undefined);
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItemId]);

    // ---- pointer gestures ---------------------------------------------------

    function canvasPoint(e: {clientX: number; clientY: number}): [number, number] {
        const rect = canvasRef.current!.getBoundingClientRect();
        return [e.clientX - rect.left, e.clientY - rect.top];
    }

    function screenToWorld(sx: number, sy: number): [number, number] {
        const canvas = canvasRef.current!;
        const v = viewRef.current;
        return [
            (sx - canvas.clientWidth / 2) / v.zoom + v.centerX,
            (sy - canvas.clientHeight / 2) / v.zoom + v.centerY,
        ];
    }

    // All items under a world point, frontmost (top of painter's order) first.
    // Picking is pixel-perfect: a point inside a sprite's bounding box only
    // counts when it lands on a hittable (non-transparent) pixel of the art.
    // When the sprite's pixel mask isn't ready yet (still loading) or can't be
    // read (placeholder / tainted), we fall back to the bounding box so nothing
    // becomes unclickable.
    function hitTestAll(wx: number, wy: number): WorkshopItem[] {
        const ordered = orderItems(itemsRef.current);
        const hits: WorkshopItem[] = [];
        for (let i = ordered.length - 1; i >= 0; i--) {
            const r = itemWorldRect(ordered[i]);
            if (!r) continue;
            if (!(wx >= r.x && wx <= r.x + r.w && wy >= r.y && wy <= r.y + r.h)) continue;
            const analysis = r.bmp ? getAnalysis(ordered[i].imageKey) : null;
            if (analysis) {
                const px = Math.floor(((wx - r.x) / r.w) * analysis.natural.w);
                const py = Math.floor(((wy - r.y) / r.h) * analysis.natural.h);
                if (!maskAt(analysis, px, py)) continue;   // clicked transparent art
            }
            hits.push(ordered[i]);
        }
        return hits;
    }

    function selectItem(item: WorkshopItem) {
        setSelectedItemId(item.id);
        props.onSelectImage(item.imageKey);
    }

    // Why an item renders as a placeholder, or undefined if it resolves fine.
    function placeholderReason(item: WorkshopItem): string | undefined {
        if (!state.images[item.imageKey]) return 'not in this branch';
        if (isFailed(item.imageKey)) return 'file missing (404)';
        return undefined;
    }

    // Pivot a Ctrl-drag delta back into a normalised pivot. Dragging the
    // image moves its top-left, but the tile-centre anchor is fixed, so the
    // pivot point within the image shifts the opposite way on X and the same
    // way on Y (Unity pivots are bottom-left origin; world Y is down). Snap
    // to the source image's pixel grid for pixel-precise placement.
    function pivotFromDrag(g: Extract<Gesture, {kind: 'pivot'}>, wx: number, wy: number) {
        let x = clamp01(g.startPivot.x - (wx - g.startWX) / g.w);
        let y = clamp01(g.startPivot.y + (wy - g.startWY) / g.h);
        if (g.natW > 0) x = Math.round(x * g.natW) / g.natW;
        if (g.natH > 0) y = Math.round(y * g.natH) / g.natH;
        return {x, y};
    }

    // Alt-drag delta → scale. Right/up grows, left/down shrinks (same
    // diagonal convention as zoom); exponential, snapped to 0.01 steps.
    function scaleFromDrag(g: Extract<Gesture, {kind: 'scale'}>, clientX: number, clientY: number) {
        const d = (clientX - g.startX) - (clientY - g.startY);
        const raw = g.startScale * Math.exp(d * SCALE_PER_PIXEL);
        return clampScale(Math.round(raw * 100) / 100);
    }

    function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
        setHoverInfo(undefined);   // hide the hover tooltip while interacting
        setShowHelp(false);        // dismiss the controls popup on any canvas click
        if (e.button === 0) {
            const [sx, sy] = canvasPoint(e);
            const [wx, wy] = screenToWorld(sx, sy);

            // Ctrl + drag on the SELECTED image edits its pivot instead of
            // relocating its tile. Requires a resolved bitmap (need natural
            // dims) and the press to land on the selected sprite.
            if ((e.ctrlKey || e.metaKey) && selectedItemId !== undefined) {
                const sel = itemsRef.current.find(i => i.id === selectedItemId);
                const r = sel ? itemWorldRect(sel) : undefined;
                if (sel && r && r.bmp
                    && wx >= r.x && wx <= r.x + r.w && wy >= r.y && wy <= r.y + r.h) {
                    const meta = state.images[sel.imageKey];
                    const startPivot = props.transientPivot?.key === sel.imageKey
                        ? {x: props.transientPivot.x, y: props.transientPivot.y}
                        : {x: meta?.offsetX ?? 0.5, y: meta?.offsetY ?? 0.5};
                    e.currentTarget.setPointerCapture(e.pointerId);
                    gestureRef.current = {
                        kind: 'pivot', imageKey: sel.imageKey, startPivot,
                        startWX: wx, startWY: wy,
                        w: r.w, h: r.h, natW: r.bmp.naturalWidth, natH: r.bmp.naturalHeight,
                    };
                    return;
                }
            }

            // Alt + drag on the SELECTED image edits its scale.
            if (e.altKey && selectedItemId !== undefined) {
                const sel = itemsRef.current.find(i => i.id === selectedItemId);
                const r = sel ? itemWorldRect(sel) : undefined;
                if (sel && r && r.bmp
                    && wx >= r.x && wx <= r.x + r.w && wy >= r.y && wy <= r.y + r.h) {
                    const startScale = props.transientScale?.key === sel.imageKey
                        ? props.transientScale.value
                        : effectiveScale(state.images[sel.imageKey]?.scale);
                    e.currentTarget.setPointerCapture(e.pointerId);
                    gestureRef.current = {
                        kind: 'scale', imageKey: sel.imageKey, startScale,
                        startX: e.clientX, startY: e.clientY,
                    };
                    return;
                }
            }

            const hits = hitTestAll(wx, wy);
            if (hits.length === 0) {
                // Empty pixel (background or transparent art): deselect and pan,
                // same as a right-drag — so LMB can pan without needing RMB.
                setSelectedItemId(undefined);
                e.currentTarget.setPointerCapture(e.pointerId);
                gestureRef.current = {
                    kind: 'pan',
                    startX: e.clientX,
                    startY: e.clientY,
                    view0: viewRef.current,
                };
                return;
            }
            // Pressing the already-selected item grabs IT (so drag keeps
            // moving the same thing); otherwise the frontmost hit.
            const wasSelected = hits.some(h => h.id === selectedItemId);
            const target = wasSelected ? hits.find(h => h.id === selectedItemId)! : hits[0];
            if (!wasSelected) selectItem(target);
            const [ax, ay] = tileCenter(target.u, target.v);
            e.currentTarget.setPointerCapture(e.pointerId);
            gestureRef.current = {
                kind: 'move', itemId: target.id, grabDX: wx - ax, grabDY: wy - ay,
                startX: e.clientX, startY: e.clientY, moved: false,
                hitIds: hits.map(h => h.id), wasSelected,
            };
            return;
        }
        if (e.button !== 1 && e.button !== 2) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        gestureRef.current = {
            kind: e.button === 2 ? 'pan' : 'zoom',
            startX: e.clientX,
            startY: e.clientY,
            view0: viewRef.current,
        };
    }

    function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
        const g = gestureRef.current;
        if (!g) {
            // Idle hover: show a tooltip when over a placeholder's box.
            const [sx, sy] = canvasPoint(e);
            const [wx, wy] = screenToWorld(sx, sy);
            const top = hitTestAll(wx, wy)[0];
            const reason = top ? placeholderReason(top) : undefined;
            setHoverInfo(top && reason ? {key: top.imageKey, reason, x: sx, y: sy} : undefined);
            return;
        }

        if (g.kind === 'pivot') {
            const [sx, sy] = canvasPoint(e);
            const [wx, wy] = screenToWorld(sx, sy);
            const p = pivotFromDrag(g, wx, wy);
            g.last = p;
            props.onPreviewPivot(g.imageKey, p.x, p.y);
            return;
        }

        if (g.kind === 'scale') {
            const s = scaleFromDrag(g, e.clientX, e.clientY);
            g.last = s;
            props.onPreviewScale(g.imageKey, s);
            return;
        }

        if (g.kind === 'move') {
            if (!g.moved) {
                const slop = Math.abs(e.clientX - g.startX) + Math.abs(e.clientY - g.startY);
                if (slop <= CLICK_SLOP_PX) return;   // still a click, don't move yet
                g.moved = true;
            }
            const [sx, sy] = canvasPoint(e);
            const [wx, wy] = screenToWorld(sx, sy);
            // The grabbed point keeps its offset from the anchor; the anchor
            // snaps live to tile centres.
            const snapped = snapToTile(wx - g.grabDX, wy - g.grabDY);
            setItems(itemsRef.current.map(i =>
                i.id === g.itemId && (i.u !== snapped.u || i.v !== snapped.v)
                    ? {...i, u: snapped.u, v: snapped.v}
                    : i));
            return;
        }

        const dx = e.clientX - g.startX;
        const dy = e.clientY - g.startY;

        if (g.kind === 'pan') {
            setView({
                ...g.view0,
                centerX: g.view0.centerX - dx / g.view0.zoom,
                centerY: g.view0.centerY - dy / g.view0.zoom,
            });
            return;
        }

        // Zoom: right/up = in, left/down = out; the world point under the
        // gesture's start position stays fixed on screen.
        const zoom = clampZoom(g.view0.zoom * Math.exp((dx - dy) * ZOOM_PER_PIXEL));
        const rect = e.currentTarget.getBoundingClientRect();
        const ax = g.startX - rect.left - rect.width / 2;    // anchor, centre-relative
        const ay = g.startY - rect.top - rect.height / 2;
        const anchorWorldX = ax / g.view0.zoom + g.view0.centerX;
        const anchorWorldY = ay / g.view0.zoom + g.view0.centerY;
        setView({
            zoom,
            centerX: anchorWorldX - ax / zoom,
            centerY: anchorWorldY - ay / zoom,
        });
    }

    function onPointerEnd() {
        const g = gestureRef.current;
        if (!g) return;
        gestureRef.current = undefined;
        if (g.kind === 'pivot') {
            const p = g.last ?? g.startPivot;
            props.onCommitPivot(g.imageKey, p.x, p.y);
            return;
        }
        if (g.kind === 'scale') {
            props.onCommitScale(g.imageKey, g.last ?? g.startScale);
            return;
        }
        if (g.kind !== 'move') {
            localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(viewRef.current));
            return;
        }
        if (g.moved) {
            persistItems(itemsRef.current);
            return;
        }
        // Clean click on an already-selected stack: cycle to the next image
        // under the cursor (front to back, wrapping).
        if (g.wasSelected && g.hitIds.length > 1) {
            const nextId = g.hitIds[(g.hitIds.indexOf(g.itemId) + 1) % g.hitIds.length];
            const next = itemsRef.current.find(i => i.id === nextId);
            if (next) selectItem(next);
        }
    }

    // ---- drop from the thumbnail grid ---------------------------------------

    function ghostFromEvent(e: React.DragEvent<HTMLCanvasElement>): {u: number; v: number} {
        const [sx, sy] = canvasPoint(e);
        const [wx, wy] = screenToWorld(sx, sy);
        return snapToTile(wx, wy);
    }

    function onDragOver(e: React.DragEvent<HTMLCanvasElement>) {
        if (!props.draggingImageKey) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        const next = ghostFromEvent(e);
        if (!ghost || ghost.u !== next.u || ghost.v !== next.v) setGhost(next);
    }

    function onDrop(e: React.DragEvent<HTMLCanvasElement>) {
        const key = props.draggingImageKey ?? e.dataTransfer.getData('text/plain');
        setGhost(undefined);
        if (!key) return;
        e.preventDefault();
        const {u, v} = ghostFromEvent(e);
        const id = itemsRef.current.reduce((m, i) => Math.max(m, i.id), 0) + 1;
        persistItems([...itemsRef.current, {id, imageKey: key, u, v}]);
        setSelectedItemId(id);
    }

    function resetView() {
        setView(DEFAULT_VIEW);
        localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(DEFAULT_VIEW));
    }

    // World bounding box of every placed item. Uses each sprite's rect; for
    // items whose bitmap hasn't resolved (or is missing) it falls back to the
    // tile centre so they still count toward the fit.
    function itemsBounds(): {minX: number; minY: number; maxX: number; maxY: number} | undefined {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const item of itemsRef.current) {
            const r = itemWorldRect(item);
            if (r) {
                minX = Math.min(minX, r.x); minY = Math.min(minY, r.y);
                maxX = Math.max(maxX, r.x + r.w); maxY = Math.max(maxY, r.y + r.h);
            } else {
                const [ax, ay] = tileCenter(item.u, item.v);
                minX = Math.min(minX, ax); minY = Math.min(minY, ay);
                maxX = Math.max(maxX, ax); maxY = Math.max(maxY, ay);
            }
        }
        return Number.isFinite(minX) ? {minX, minY, maxX, maxY} : undefined;
    }

    function zoomToFit() {
        const b = itemsBounds();
        const canvas = canvasRef.current;
        if (!b || !canvas) return;
        const pad = 24;   // screen-px breathing room around the content
        const bw = Math.max(1, b.maxX - b.minX);
        const bh = Math.max(1, b.maxY - b.minY);
        const zoom = clampZoom(Math.min(
            (canvas.clientWidth - 2 * pad) / bw,
            (canvas.clientHeight - 2 * pad) / bh,
        ));
        const next = {zoom, centerX: (b.minX + b.maxX) / 2, centerY: (b.minY + b.maxY) / 2};
        setView(next);
        localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(next));
    }

    // ---- save / load (blob container 'workshops', branch-agnostic) ---------

    async function fetchWorkshopList(): Promise<string[] | undefined> {
        const response = await get(`${apiBindings.api}getWorkshops`, undefined);
        if (!response.success || !Array.isArray(response.data)) return undefined;
        return response.data as string[];
    }

    // Saves under `name`. confirmOverwrite=false skips the are-you-sure when
    // re-saving to this workshop's own file (the save-before-load path).
    async function performSave(name: string, confirmOverwrite: boolean): Promise<boolean> {
        const trimmed = name.trim();
        if (!WORKSHOP_NAME_PATTERN.test(trimmed)) {
            setNotification({
                title: 'Invalid Name',
                message: 'Workshop names can use letters, digits, spaces, dashes and underscores (max 64).',
            });
            return false;
        }
        const savingDefault = isDefaultName(trimmed);
        // Only Admins may write the shared default workshop (mirrors the backend
        // guard — the backend rejects it regardless, this is the friendly path).
        if (savingDefault && !canSaveDefaultWorkshop) {
            setNotification({
                title: 'Not Allowed',
                message: 'Only Admins can save the default workshop. Choose a different name.',
            });
            return false;
        }
        const list = await fetchWorkshopList();
        if (list === undefined) {
            setNotification({title: 'Save Failed', message: 'Could not reach workshop storage.'});
            return false;
        }
        if (savingDefault) {
            // Admin overwriting the default everyone sees — always warn, even on
            // the no-confirm save-before-load path.
            const proceed = await confirm({
                title: 'Overwrite the default workshop?',
                body: <p>This will replace the <strong>default</strong> workshop that everyone sees when they
                    first open the Workshop without loading one of their own. Continue?</p>,
                confirmText: 'Overwrite default',
                danger: true,
            });
            if (!proceed) return false;
        } else if (confirmOverwrite && list.includes(trimmed)) {
            const proceed = await confirm({
                title: 'Overwrite workshop?',
                body: <p>A workshop named <strong>{trimmed}</strong> already exists. Overwrite it?</p>,
                confirmText: 'Overwrite',
                danger: true,
            });
            if (!proceed) return false;
        }
        const payload = JSON.stringify(
            {version: WORKSHOP_SCHEMA_VERSION, name: trimmed, items: itemsRef.current});
        const response = await post(`${apiBindings.api}setWorkshop?name=${encodeURIComponent(trimmed)}`, payload, undefined);
        if (!response.success) {
            setNotification({title: 'Save Failed', message: response.data?.message ?? 'Could not save the workshop.'});
            return false;
        }
        setWorkshopName(trimmed);
        return true;
    }

    async function submitSaveDialog() {
        if (!saveDialog) return;
        const ok = await performSave(saveDialog.name, true);
        if (!ok) return;   // keep the dialog open (rename / retry)
        const continueLoad = saveDialog.continueLoad;
        setSaveDialog(undefined);
        if (continueLoad) await showLoadPicker();
    }

    async function showLoadPicker() {
        const list = await fetchWorkshopList();
        if (list === undefined) {
            setNotification({title: 'Load Failed', message: 'Could not reach workshop storage.'});
            return;
        }
        if (list.length === 0) {
            setNotification({title: 'No Workshops', message: 'No saved workshops found.'});
            return;
        }
        const sorted = [...list].sort((a, b) => a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'}));
        disarmDelete();
        setLoadDialog({names: sorted, filter: '', selected: undefined});
    }

    async function loadWorkshopByName(name: string) {
        const response = await get(`${apiBindings.api}getWorkshop?name=${encodeURIComponent(name)}`, undefined);
        const loaded = migrateItems(
            sanitizeItems(response.data?.items), payloadVersion(response.data?.version));
        if (!response.success || loaded.length === 0) {
            setNotification({title: 'Load Failed', message: `Could not load workshop "${name}".`});
            return;
        }
        setLoadDialog(undefined);
        persistItems(loaded);
        setSelectedItemId(undefined);
        setWorkshopName(name);
    }

    function disarmDelete() {
        window.clearTimeout(deleteArmTimer.current);
        setDeleteArmed(false);
    }

    function onDeleteClick(name: string) {
        if (!deleteArmed) {
            setDeleteArmed(true);
            window.clearTimeout(deleteArmTimer.current);
            deleteArmTimer.current = window.setTimeout(() => setDeleteArmed(false), 3000);
            return;
        }
        disarmDelete();
        void performDeleteWorkshop(name);
    }

    async function performDeleteWorkshop(name: string) {
        const response = await deleteRequest(`${apiBindings.api}deleteWorkshop?name=${encodeURIComponent(name)}`, '', undefined);
        if (!response.success) {
            setNotification({title: 'Delete Failed', message: response.data?.message ?? `Could not delete workshop "${name}".`});
            return;
        }
        setLoadDialog(d => d ? {...d, names: d.names.filter(n => n !== name), selected: undefined} : d);
    }

    async function loadWorkshopFlow() {
        if (itemsRef.current.length > 0) {
            const action = await choose({
                title: 'Save current workshop?',
                body: <p>The current workshop is not empty. Save it before loading another?</p>,
                options: [
                    {text: 'Save first', value: 'save'},
                    {text: 'Don\'t save', value: 'discard'},
                    {text: 'Cancel', value: 'cancel'},
                ],
            });
            if (action === undefined || action === 'cancel') return;
            if (action === 'save') {
                if (workshopName && !isDefaultName(workshopName)) {
                    if (!await performSave(workshopName, false)) return;
                } else {
                    // No name yet (or the default, which most users can't re-save)
                    // — go through the save dialog with a blank name, which chains
                    // back into the picker on success.
                    setSaveDialog({name: '', continueLoad: true});
                    return;
                }
            }
        }
        await showLoadPicker();
    }

    function disarmClear() {
        window.clearTimeout(clearArmTimer.current);
        setClearArmed(false);
    }

    function onClearClick() {
        if (!clearArmed) {
            setClearArmed(true);
            window.clearTimeout(clearArmTimer.current);
            clearArmTimer.current = window.setTimeout(() => setClearArmed(false), 3000);
            return;
        }
        disarmClear();
        persistItems([]);
        setSelectedItemId(undefined);
    }

    return (
        <div className={'layout vertical box'}
             style={{flexGrow: 1, minHeight: 0, padding: 10, gap: 8}}>
            <div className={'layout horizontal center stretch'}>
                <p style={{fontWeight: 600}}>
                    Workshop{workshopName ? <span className={'muted-text'}> — {workshopName}</span> : ''}
                </p>
                <div className={'layout horizontal center'} style={{gap: 10}}>
                    <p className={'muted-text'} style={{fontSize: 12}}>{Math.round(view.zoom * 100)}%</p>
                    {/* Saving to storage is a server write (Tester+). */}
                    {canServerWrite && <button title={'Save this workshop to storage'}
                            onClick={() => setSaveDialog({
                                // Pre-fill the current name, but blank it for the
                                // default workshop so saving nudges you to a new
                                // name (only Admins can re-save "default").
                                name: workshopName && !isDefaultName(workshopName) ? workshopName : '',
                                continueLoad: false,
                            })}>
                        <span className={'fa-solid fa-floppy-disk'}/>
                    </button>}
                    <button title={'Load a workshop from storage'}
                            onClick={() => void loadWorkshopFlow()}>
                        <span className={'fa-solid fa-folder-open'}/>
                    </button>
                    {items.length > 0 &&
                        <button title={clearArmed
                                ? 'Click again to remove all images from the workshop'
                                : 'Remove all images from the workshop'}
                                onClick={onClearClick}
                                onBlur={disarmClear}
                                style={clearArmed ? {backgroundColor: '#a03030', color: 'white'} : undefined}>
                            {clearArmed ? 'Clear?' : 'Clear'}
                        </button>}
                    <button title={'Zoom to fit all images'} disabled={items.length === 0}
                            style={{opacity: items.length === 0 ? 0.5 : 1}}
                            onClick={zoomToFit}>
                        <span className={'fa-solid fa-expand'}/>
                    </button>
                    <button title={'Reset view'} onClick={resetView}>
                        <span className={'fa-solid fa-crosshairs'}/>
                    </button>
                    <div style={{position: 'relative'}}>
                        <button title={'Help'} onClick={() => setShowHelp(!showHelp)}>
                            <span className={'fa-solid fa-circle-question'}/>
                        </button>
                        {showHelp &&
                            <div className={'layout vertical box'}
                                 style={{
                                     position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                                     width: 640, padding: 12, gap: 6, zIndex: 100,
                                 }}>
                                <p style={{fontWeight: 600}}>Workshop controls</p>
                                <p className={'muted-text'}>• Drag images in from the grid on the right to place them</p>
                                <p className={'muted-text'}>• Left-click: select — click again to cycle through overlapping images</p>
                                <p className={'muted-text'}>• Left-drag: move the image to another tile</p>
                                <p className={'muted-text'}>• {CTRL_KEY_LABEL} + drag: nudge the selected image's pivot (pixel-precise)</p>
                                <p className={'muted-text'}>• {ALT_KEY_LABEL} + drag: scale the selected image (right/up bigger)</p>
                                <p className={'muted-text'}>• Del / Backspace: remove the selected image</p>
                                <p className={'muted-text'}>• Right-drag, or left-drag on empty space: pan</p>
                                <p className={'muted-text'}>• Wheel, pinch, or middle-drag: zoom (right/up in, left/down out)</p>
                            </div>}
                    </div>
                </div>
            </div>
            <div style={{flexGrow: 1, minHeight: 0, position: 'relative'}}>
                <canvas ref={canvasRef}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerEnd}
                        onPointerCancel={onPointerEnd}
                        onPointerLeave={() => setHoverInfo(undefined)}
                        onDragOver={onDragOver}
                        onDrop={onDrop}
                        onDragLeave={() => setGhost(undefined)}
                        onContextMenu={e => e.preventDefault()}
                        // Block the middle-click autoscroll widget — pointer
                        // events alone don't suppress it.
                        onMouseDown={e => { if (e.button === 1) e.preventDefault(); }}
                        style={{
                            position: 'absolute', inset: 0,
                            width: '100%', height: '100%',
                            touchAction: 'none',
                        }}/>
                {hoverInfo && (
                    <div style={{
                        position: 'absolute',
                        left: hoverInfo.x + 14, top: hoverInfo.y + 14,
                        pointerEvents: 'none', zIndex: 10,
                        background: 'rgba(0, 0, 0, 0.85)', color: 'white',
                        padding: '4px 8px', borderRadius: 4,
                        fontSize: 12, whiteSpace: 'nowrap',
                        maxWidth: 'calc(100% - 28px)', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        <div>{hoverInfo.key}</div>
                        <div style={{opacity: 0.7}}>{hoverInfo.reason}</div>
                    </div>
                )}
            </div>

            {saveDialog && (
                /* Below the app-context confirm/choose overlays (zIndex 2000)
                   so the overwrite prompt renders on top of this dialog. */
                <div className={'layout vertical overlay'} style={{zIndex: 1500}}>
                    <div className={'layout vertical gap box'} style={{padding: 12, gap: 10, width: 360}}>
                        <p className={'text header'}>Save Workshop</p>
                        <input autoFocus placeholder={'Workshop name...'}
                               value={saveDialog.name}
                               onChange={e => setSaveDialog({...saveDialog, name: e.target.value})}
                               onKeyDown={e => {
                                   e.stopPropagation();
                                   if (e.key === 'Enter') void submitSaveDialog();
                                   if (e.key === 'Escape') setSaveDialog(undefined);
                               }}/>
                        <div className={'layout horizontal'} style={{justifyContent: 'flex-end', gap: 8}}>
                            <button onClick={() => setSaveDialog(undefined)}>Cancel</button>
                            <button onClick={() => void submitSaveDialog()}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {loadDialog && (() => {
                const filter = loadDialog.filter.trim().toLowerCase();
                const visible = loadDialog.names.filter(n => filter === '' || n.toLowerCase().includes(filter));
                const selected = loadDialog.selected !== undefined && visible.includes(loadDialog.selected)
                    ? loadDialog.selected : undefined;
                // Enter loads the selection, or the single remaining match.
                const enterTarget = selected ?? (visible.length === 1 ? visible[0] : undefined);
                return (
                    <div className={'layout vertical overlay'} style={{zIndex: 1500}}>
                        <div className={'layout vertical gap box'} style={{padding: 12, gap: 10, width: 380}}>
                            <p className={'text header'}>Load Workshop</p>
                            <input autoFocus placeholder={'Filter...'}
                                   value={loadDialog.filter}
                                   onChange={e => setLoadDialog({...loadDialog, filter: e.target.value})}
                                   onKeyDown={e => {
                                       e.stopPropagation();
                                       if (e.key === 'Enter' && enterTarget !== undefined) void loadWorkshopByName(enterTarget);
                                       if (e.key === 'Escape') setLoadDialog(undefined);
                                   }}/>
                            <div className={'layout vertical'}
                                 style={{maxHeight: 'min(80vh, calc(100vh - 220px))', minHeight: 160, overflowY: 'auto', gap: 2}}>
                                {visible.length === 0 &&
                                    <p className={'muted-text'} style={{fontStyle: 'italic', padding: 4}}>
                                        No workshops match the filter.
                                    </p>}
                                {visible.map(name => (
                                    <p key={name}
                                       onClick={() => {
                                           disarmDelete();
                                           setLoadDialog({...loadDialog, selected: name});
                                       }}
                                       onDoubleClick={() => void loadWorkshopByName(name)}
                                       style={{
                                           cursor: 'pointer', padding: '4px 8px', borderRadius: 4,
                                           userSelect: 'none',
                                           backgroundColor: name === selected ? 'rgba(255,255,255,0.12)' : undefined,
                                           color: name === selected ? 'var(--tint-color)' : undefined,
                                       }}>
                                        {name}
                                    </p>
                                ))}
                            </div>
                            <div className={'layout horizontal center stretch'} style={{gap: 8}}>
                                {/* Deleting from storage is a server write (Tester+). */}
                                {canServerWrite && <button disabled={selected === undefined}
                                        title={deleteArmed
                                            ? 'Click again to permanently delete this workshop'
                                            : 'Delete the selected workshop from storage'}
                                        onBlur={disarmDelete}
                                        style={{
                                            opacity: selected === undefined ? 0.5 : 1,
                                            ...(deleteArmed ? {backgroundColor: '#a03030', color: 'white'} : {}),
                                        }}
                                        onClick={() => selected !== undefined && onDeleteClick(selected)}>
                                    {deleteArmed ? 'Delete?' : 'Delete'}
                                </button>}
                                <div className={'layout horizontal'} style={{gap: 8}}>
                                    <button onClick={() => setLoadDialog(undefined)}>Cancel</button>
                                    <button disabled={selected === undefined}
                                            style={{opacity: selected === undefined ? 0.5 : 1}}
                                            onClick={() => selected !== undefined && void loadWorkshopByName(selected)}>
                                        Load
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

export default WorkshopView;
